import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { brand, radius } from "../../theme/businessFlowsTheme";

// Filters for advanced search: favourites and object types.
//
// `unavailableMessage` is set when the types aren't known because the
// database couldn't be reached. With no types and no message, they really are
// all hidden - which is an admin's decision, and says so.
export default function FilterPanel({ types, checkedTypes, onTypesChange, favoritesOnly, onFavoritesChange, unavailableMessage = "" }) {
  const allChecked = types.length > 0 && checkedTypes.length === types.length;
  const someChecked = checkedTypes.length > 0 && !allChecked;
  const hasFilters = favoritesOnly || checkedTypes.length > 0;

  const toggleType = (name) =>
    onTypesChange(checkedTypes.includes(name) ? checkedTypes.filter((t) => t !== name) : [...checkedTypes, name]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: "1rem" }}>
          Filters
        </Typography>
        {hasFilters && (
          <Button
            size="small"
            onClick={() => {
              onTypesChange([]);
              onFavoritesChange(false);
            }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: radius.card,
            bgcolor: favoritesOnly ? brand.mark : brand.canvas,
            border: `1px solid ${favoritesOnly ? brand.signal : brand.line}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <StarRoundedIcon sx={{ color: favoritesOnly ? brand.signalDeep : "text.secondary", fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }} component="label" htmlFor="as-favorites">
              My favourites
            </Typography>
          </Stack>
          <Switch
            id="as-favorites"
            size="small"
            checked={favoritesOnly}
            onChange={(e) => onFavoritesChange(e.target.checked)}
          />
        </Box>
      </Box>

      <Divider />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Types
        </Typography>
        {checkedTypes.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {checkedTypes.length} selected
          </Typography>
        )}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 2 }}>
        <FormControlLabel
          sx={{ mx: 0, width: "100%", borderRadius: radius.control, "&:hover": { bgcolor: brand.canvas } }}
          control={
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(e) => onTypesChange(e.target.checked ? types.map((t) => t.name) : [])}
            />
          }
          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>All types</Typography>}
        />
        {types.map((type) => (
          <FormControlLabel
            key={type.name}
            sx={{ mx: 0, width: "100%", borderRadius: radius.control, "&:hover": { bgcolor: brand.canvas } }}
            control={
              <Checkbox size="small" checked={checkedTypes.includes(type.name)} onChange={() => toggleType(type.name)} />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: type.colour || brand.muted, flexShrink: 0 }} />
                <Typography variant="body2">{type.label || type.name}</Typography>
              </Stack>
            }
          />
        ))}
        {types.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
            {unavailableMessage || "Every type is hidden from search in this database. An admin can show types under Console → Views."}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
